<script setup>
// First-run onboarding shell (MDL-323). A generic, skippable multi-step wizard.
//
// It owns step navigation and the skip affordances; step *content* is provided
// by the consumer through a named slot per step key, so later stories
// (WiFi / device basics / MIDI / first patch) drop their forms in without
// touching this shell. See spec 144080898 §2, §3.
//
// Events:
//   finish   — the user advanced past the last step (completed the flow)
//   skip-all — the user chose to skip the entire setup
//   step     — (index, key) emitted whenever the active step changes
//
// Both finish and skip-all are terminal from the shell's point of view: the
// parent is responsible for the exit lifecycle (seed a patch if none, persist)
// and for unmounting the wizard.
//
// A step can block leaving it (both Next and Skip) via the `canProceed` prop
// — the wizard is content-agnostic, so whoever mounts it wires that prop to
// the current step's own validity signal (see OnboardingWifiStep).

import { computed, ref } from 'vue';

const props = defineProps({
  // Ordered steps: [{ key: 'wifi', label: 'WiFi' }, …]. May be empty.
  steps: {
    type: Array,
    default: () => [],
  },
  title: {
    type: String,
    default: 'Midulate',
  },
  // Controlled by whoever mounts the wizard: false blocks leaving the current
  // step (both Next and Skip — from here, either one carries forward whatever
  // the step's slot content already wrote in place, e.g. a WiFi form that
  // mutates settings live) until the step's own content is valid. The wizard
  // doesn't know why a step is blocked — that's the consuming step's concern
  // (see OnboardingWifiStep's update:invalid). Back is never blocked; leaving
  // backwards doesn't skip anything, the step is revisited before Finish.
  canProceed: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['finish', 'skip-all', 'step']);

const index = ref(0);

const hasSteps = computed(() => props.steps.length > 0);
const currentStep = computed(() => props.steps[index.value] ?? null);
const isFirst = computed(() => index.value === 0);
const isLast = computed(() => index.value >= props.steps.length - 1);

function goTo(i) {
  const clamped = Math.max(0, Math.min(i, props.steps.length - 1));
  index.value = clamped;
  const step = props.steps[clamped];
  emit('step', clamped, step ? step.key : null);
}

function next() {
  if (!props.canProceed) return;
  if (isLast.value) {
    emit('finish');
    return;
  }
  goTo(index.value + 1);
}

function back() {
  if (!isFirst.value) goTo(index.value - 1);
}

// Per-step skip: advance without the step taking any action. On the last step
// it simply finishes the flow. Gated the same as Next — see `canProceed`.
function skipStep() {
  next();
}

function skipAll() {
  emit('skip-all');
}

defineExpose({ index, goTo, next, back });
</script>

<template>
  <div class="onboarding-wizard flex-1 flex flex-col overflow-hidden" data-testid="onboarding-wizard">
    <!-- Header: title + progress + global skip -->
    <header class="flex-shrink-0 border-b border-base-300 px-4 py-3">
      <h1 class="text-center text-3xl font-semibold font-russo uppercase p-4 tracking-wider">{{ title }}</h1>

      <!-- Step indicator. daisyUI's `.steps` is `display: inline-grid`, so
           `mx-auto` on the <ol> itself has no centering effect (auto-margin
           centering only applies to block-level boxes) — wrap it in an
           ordinary block div instead, matching the header/main/footer pattern. -->
      <div v-if="hasSteps" class="max-w-2xl mx-auto mt-3">
        <ol class="steps w-full" data-testid="onboarding-steps">
          <li
            v-for="(step, i) in steps"
            :key="step.key"
            class="step"
            :class="{ 'step-primary': i <= index }"
          >
            {{ step.label }}
          </li>
        </ol>
      </div>
    </header>

    <!-- Step body. min-h-0 is load-bearing: flex items default to
         min-height:auto, so without it a flex-1 + overflow-y-auto child won't
         actually shrink to scroll internally — it just grows to fit its
         content instead, pushing the footer out of the fixed-height wizard.
         Short steps (Welcome, WiFi) never hit this since their content fits
         anyway; the MIDI step's 16-channel grid and the expanded First-patch
         step are tall enough to expose it. -->
    <main class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
      <div class="max-w-2xl mx-auto p-4">
        <template v-if="currentStep">
          <slot :name="currentStep.key" :step="currentStep" :index="index">
            <!-- Fallback when a consumer hasn't provided a slot for this step. -->
            <p class="text-base-content/60">{{ currentStep.label }}</p>
          </slot>
        </template>
        <template v-else>
          <slot name="empty">
            <p class="text-base-content/60">Nothing to set up.</p>
          </slot>
        </template>
      </div>
    </main>

    <!-- Footer: navigation -->
    <footer class="flex-shrink-0 border-t border-base-300 px-4 py-3">
      <div class="max-w-2xl mx-auto flex items-center justify-between gap-2">
        <button
          type="button"
          class="btn btn-ghost btn-sm"
          :disabled="isFirst"
          data-testid="onboarding-back"
          @click="back"
        >
          Back
        </button>
        <div class="flex items-center gap-2">
          <button
            v-if="hasSteps"
            type="button"
            class="btn btn-ghost btn-sm"
            :disabled="!canProceed"
            data-testid="onboarding-skip-step"
            @click="skipStep"
          >
            Skip
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="!canProceed"
            data-testid="onboarding-next"
            @click="next"
          >
            {{ isLast ? 'Finish' : 'Next' }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
/* daisyUI's steps component hardcodes its marker/row sizing (steps.css) and
   exposes no size variant (unlike btn-xs etc), so shrinking it means
   overriding those rules directly. These are plain, unlayered component
   styles — Cascade Layers give unlayered CSS priority over daisyUI's
   `@layer daisyui...` rules regardless of selector specificity, so no
   !important or specificity gymnastics are needed here. */
.steps .step {
  font-size: 0.75rem;
  min-width: 3.5rem;
  grid-template-rows: 30px 1fr;
}

.steps .step::before {
  height: 0.375rem;
}

.steps .step::after {
  width: 1.5rem;
  height: 1.5rem;
  font-size: 0.75rem;
}
</style>
